"use client"
import * as ModalPrimitive from "@radix-ui/react-dialog"
import useMergedRef from "@react-hook/merged-ref"
import { motion } from "motion/react"
import type React from "react"
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type Ref,
  useCallback,
  useRef,
  useState,
} from "react"
import { tv, type VariantProps } from "tailwind-variants"
import { useCallbackRef } from "../../hooks"
import { ArrowBack } from "../../icons/material-symbols/outlined/ArrowBack"
import { Close } from "../../icons/material-symbols/outlined/Close"
import { overlayVariants } from "../../styles/utils/overlay"
import { customScrollbarClassname } from "../../styles/utils/scrollbar"
import { classNames } from "../../utils"
import { AnimateHeight } from "../AnimateHeight"
import { Block, type BlockProps } from "../Block"
import {
  BottomSheet,
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetFooterButton,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
} from "../BottomSheet"
import { Button, type ButtonProps } from "../Button"
import { FlexCenter, type FlexCenterProps } from "../FlexCenter"
import { FlexColumn } from "../FlexColumn"
import { Media, useIsLessThanMd } from "../Media"
import { TextDisplay } from "../Text"

export type ModalProps = Pick<
  ComponentProps<typeof ModalPrimitive.Root>,
  "open" | "defaultOpen" | "onOpenChange" | "modal"
> &
  VariantProps<typeof modalVariants> &
  Omit<ComponentProps<typeof ModalPrimitive.Content>, "content"> & {
    content: React.ReactNode
    withCloseIcon?: boolean
    onBack?: () => void
    disableHeightAnimation?: boolean
    disableWidthTransition?: boolean
    overrides?: {
      Overlay?: Partial<ComponentProps<typeof ModalPrimitive.Overlay>>
      CloseButton?: {
        className?: string
      }
      BackButton?: {
        className?: string
      }
    }
  }

const modalVariants = tv({
  base: classNames(
    "relative z-modal rounded-xl border border-border-1 bg-bg-app ease-out-quint",
    "max-w-[calc(100vw-32px)]",
    "data-[state=closed]:animate-out data-[state=open]:animate-in",
    "data-[state=closed]:duration-300 data-[state=open]:duration-300",
    "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
    "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
    "focus-visible:outline-hidden",
    "overflow-hidden",
  ),
  variants: {
    size: {
      sm: "w-[420px]",
      md: "w-[480px]",
      lg: "w-[550px]",
      xl: "m-0 h-[calc(100vh-64px)] w-[calc(100vw-64px)] max-w-none p-0",
    },
  },
})

const MODAL_CONTENT_SELECTOR = "[data-id='ModalContent']"
const MODAL_FOOTER_SELECTOR = "[data-id='ModalFooter']"

function Modal({
  open,
  defaultOpen,
  onOpenChange,
  content,
  children,
  className,
  modal,
  withCloseIcon = true,
  onBack,
  disableHeightAnimation = false,
  disableWidthTransition = false,
  overrides,
  size = "lg",
  ...props
}: ModalProps) {
  // We need to keep track of the open state internally so we can render the bottom sheet with the correct state on mobile
  const [openedState, setOpenedState] = useState(defaultOpen ?? open)
  const resolvedOpenState = open === undefined ? openedState : open
  // This is needed due to a bug seemingly in vaul where unmounting the component through Media still locks the cursor.
  const isLessThanMd = useIsLessThanMd()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const handleOnOpenChange = useCallback(
    (open: boolean) => {
      setOpenedState(open)
      onOpenChange?.(open)
    },
    [onOpenChange],
  )

  return (
    <>
      <Media lessThan="md">
        <BottomSheet
          className={className}
          content={content}
          disableHeightAnimation={disableHeightAnimation}
          modal={modal}
          onBack={onBack}
          onOpenChange={handleOnOpenChange}
          open={isLessThanMd && resolvedOpenState}
          overrides={overrides}
          withCloseIcon={withCloseIcon}
        >
          {children}
        </BottomSheet>
      </Media>
      <Media greaterThanOrEqual="md">
        <ModalPrimitive.Root
          defaultOpen={defaultOpen}
          modal={modal}
          onOpenChange={handleOnOpenChange}
          open={!isLessThanMd && resolvedOpenState}
        >
          <ModalPrimitive.Trigger asChild ref={triggerRef}>
            {children}
          </ModalPrimitive.Trigger>
          <ModalPrimitive.Portal>
            <ModalPrimitive.Overlay
              data-testid="Overlay"
              {...overrides?.Overlay}
              className={classNames(
                overlayVariants(),
                "z-modal flex items-center justify-center",
                overrides?.Overlay?.className,
              )}
            >
              <ModalPrimitive.Content
                aria-describedby={undefined}
                className={classNames(
                  modalVariants({ size }),
                  disableWidthTransition && "!transition-none",
                  className,
                )}
                data-id="ModalContent"
                {...props}
              >
                {disableHeightAnimation ? (
                  // TODO: Add the same width to below?
                  <FlexColumn className="max-h-[calc(100vh-64px)] w-full">
                    {content}
                  </FlexColumn>
                ) : (
                  <AnimateHeight>
                    <FlexColumn className="max-h-[calc(100vh-64px)]">
                      {content}
                    </FlexColumn>
                  </AnimateHeight>
                )}

                {onBack && (
                  <div
                    className={classNames(
                      "absolute top-4 left-4 z-modal rounded-full border border-border-1 p-1",
                      overrides?.BackButton?.className,
                    )}
                    onClick={onBack}
                  >
                    <ArrowBack fill="interactive-primary-styles" size={24} />
                  </div>
                )}

                {withCloseIcon && (
                  <ModalPrimitive.Close
                    className={classNames(
                      "absolute top-6 right-6 z-modal rounded-full border border-border-1 p-1",
                      overrides?.CloseButton?.className,
                    )}
                  >
                    <Close fill="interactive-primary-styles" size={24} />
                  </ModalPrimitive.Close>
                )}
              </ModalPrimitive.Content>
            </ModalPrimitive.Overlay>
          </ModalPrimitive.Portal>
        </ModalPrimitive.Root>
      </Media>
    </>
  )
}

const modalHeaderVariants = tv({
  base: "px-6 pb-6",
  variants: {
    variant: {
      default: "pt-6",
      heroMedia: "flex-col justify-center pt-12",
    },
  },
})

export const ModalHeader = ({
  className,
  variant = "default",
  ...props
}: FlexCenterProps & VariantProps<typeof modalHeaderVariants>) => {
  return (
    <>
      <Media lessThan="md">
        <BottomSheetHeader className={className} variant={variant} {...props} />
      </Media>
      <Media greaterThanOrEqual="md">
        <FlexCenter
          className={classNames(modalHeaderVariants({ variant }), className)}
          {...props}
        />
      </Media>
    </>
  )
}

export function ModalHeaderTitle({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof ModalPrimitive.Title>) {
  return (
    <>
      <Media lessThan="md">
        <BottomSheetHeaderTitle className={className} {...props}>
          {children}
        </BottomSheetHeaderTitle>
      </Media>
      <Media greaterThanOrEqual="md">
        <ModalPrimitive.Title asChild className={className} {...props}>
          <TextDisplay asChild size="sm">
            <h4>{children}</h4>
          </TextDisplay>
        </ModalPrimitive.Title>
      </Media>
    </>
  )
}

export function ModalBody({ className, children, ref, ...props }: BlockProps) {
  const [bodyRef, setBodyRef] = useCallbackRef<HTMLDivElement>()
  const mergedRef = useMergedRef(ref as Ref<HTMLDivElement>, setBodyRef)
  const hasFooter = Boolean(
    bodyRef?.current
      ?.closest(MODAL_CONTENT_SELECTOR)
      ?.querySelector(MODAL_FOOTER_SELECTOR),
  )

  return (
    <>
      <Media lessThan="md">
        <BottomSheetBody className={className} ref={ref} {...props}>
          {children}
        </BottomSheetBody>
      </Media>
      <Media greaterThanOrEqual="md">
        <Block
          className={classNames(
            "flex flex-col overflow-y-auto px-6",
            hasFooter ? "pb-6" : "pb-10",
            customScrollbarClassname,
            className,
          )}
          ref={mergedRef}
          {...props}
        >
          {children}
        </Block>
      </Media>
    </>
  )
}

export function ModalFooter({
  children,
  className,
  ...props
}: ComponentProps<typeof motion.div>) {
  return (
    <>
      <Media lessThan="md">
        <BottomSheetFooter className={className} {...props}>
          {children}
        </BottomSheetFooter>
      </Media>
      <Media greaterThanOrEqual="md">
        <motion.div
          className={classNames(
            "flex w-full gap-3 border-border-1 border-t bg-bg-primary p-4",
            className,
          )}
          data-id="ModalFooter"
          layout="position"
          transition={{ type: "spring", bounce: 0, duration: 0.3 }}
          {...props}
        >
          {children}
        </motion.div>
      </Media>
    </>
  )
}

export function ModalFooterButton({ className, ...rest }: ButtonProps) {
  return (
    <>
      <Media lessThan="md">
        <BottomSheetFooterButton className={className} {...rest} />
      </Media>
      <Media greaterThanOrEqual="md">
        <Button className={classNames("w-full", className)} {...rest} />
      </Media>
    </>
  )
}

/**
 * A window overlaid on either the primary window or another dialog window, rendering the content underneath inert.
 * Turns into a bottom sheet on mobile.
 */
export { Modal }
